import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  Grid,
  MenuItem,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import AppToast from "../../components/AppToast";
import MDEditor, { commands } from '@uiw/react-md-editor';
import ImagePreviewModal from "../../components/ui/ImagePreviewModal";

export default function EditMom() {
  const { projectId, momId } = useParams();
  const { token, userData } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    meetingDate: "",
    discussionPoints: [],
    status: "DRAFT",
    adminRemarks: ""
  });

  // Inline Editor State
  const [isAddingPoint, setIsAddingPoint] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentPoint, setCurrentPoint] = useState({
    pointDate: new Date().toISOString().split("T")[0],
    discussion: "",
    instructionImages: [],
    instructionImagesFiles: [],
    actionable: userData?.name || "",
    responsibility: "",
    status: "Open",
    closeDate: "",
    remarks: "",
  });

  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });
  const [previewImage, setPreviewImage] = useState(null);
  const isAdmin = userData?.role === "ADMIN" || userData?.role === "SUPERADMIN";

  // Fetch project & mom details
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const config = { headers: { Authorization: token ? `Bearer ${token}` : undefined } };
        
        const [projectRes, momRes] = await Promise.all([
          axios.get(`${url}/api/inhouse-projects/fetch-by/${projectId}`, config),
          axios.get(`${url}/api/mom/${momId}`, config)
        ]);

        if (projectRes.data.project) setProject(projectRes.data.project);
        
        if (momRes.data.mom) {
          const mom = momRes.data.mom;
          setFormData({
            serialNumber: mom.serialNumber,
            meetingDate: mom.meetingDate ? new Date(mom.meetingDate).toISOString().split("T")[0] : "",
            discussionPoints: mom.discussionPoints && mom.discussionPoints.length ? 
              mom.discussionPoints.map((dp, idx) => ({
                pointNumber: dp.pointNumber || idx + 1,
                pointDate: dp.pointDate ? new Date(dp.pointDate).toISOString().split("T")[0] : "",
                discussion: dp.instruction || dp.discussion || dp.actionable || dp.topic || "",
                instructionImages: dp.instructionImages || [],
                instructionImagesFiles: [],
                actionable: dp.actionable || dp.actionBy || "",
                responsibility: dp.responsibility || dp.givenBy || "",
                status: dp.status || "Open",
                closeDate: dp.closeDate ? new Date(dp.closeDate).toISOString().split("T")[0] : "",
                remarks: dp.remarks || ""
              })) : 
              [],
            status: mom.status || "DRAFT",
            adminRemarks: mom.adminRemarks || ""
          });
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setToast({ open: true, type: "error", title: "Error", message: "Failed to load data" });
      } finally {
        setLoading(false);
      }
    };
    if (projectId && momId && token) fetchData();
  }, [projectId, momId, token]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ----------------------------------------
  // Inline Point Editor Logic
  // ----------------------------------------
  const handleOpenAddPoint = () => {
    setCurrentPoint({
      pointDate: new Date().toISOString().split("T")[0],
      discussion: "",
      instructionImages: [],
      instructionImagesFiles: [],
      actionable: userData?.name || "",
      responsibility: "",
      status: "Open",
      closeDate: "",
      remarks: "",
    });
    setEditingIndex(null);
    setIsAddingPoint(true);
  };

  const handleEditPoint = (index) => {
    setCurrentPoint(formData.discussionPoints[index]);
    setEditingIndex(index);
    setIsAddingPoint(true);
  };

  const handleDeletePoint = (index) => {
    const newPoints = [...formData.discussionPoints];
    newPoints.splice(index, 1);
    // Re-number
    newPoints.forEach((p, i) => { p.pointNumber = i + 1; });
    setFormData({ ...formData, discussionPoints: newPoints });
  };

  const handleCurrentPointChange = (field, value) => {
    setCurrentPoint({ ...currentPoint, [field]: value });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const textarea = e.target;
      const start = textarea.selectionStart;
      const value = textarea.value;
      
      const beforeCursor = value.substring(0, start);
      const currentLineStart = beforeCursor.lastIndexOf('\n') + 1;
      const currentLine = beforeCursor.substring(currentLineStart);
      
      const bulletMatch = currentLine.match(/^(\s*)([-*+])\s(.*)$/);
      const numberMatch = currentLine.match(/^(\s*)(\d+)\.\s(.*)$/);
      
      if (bulletMatch || numberMatch) {
        e.preventDefault();
        
        let insertText = '';
        if (bulletMatch) {
          if (!bulletMatch[3].trim()) {
            const newValue = value.substring(0, currentLineStart) + value.substring(start);
            handleCurrentPointChange('discussion', newValue);
            setTimeout(() => {
              textarea.selectionStart = textarea.selectionEnd = currentLineStart;
            }, 0);
            return;
          }
          insertText = `\n${bulletMatch[1]}${bulletMatch[2]} `;
        } else if (numberMatch) {
          if (!numberMatch[3].trim()) {
            const newValue = value.substring(0, currentLineStart) + value.substring(start);
            handleCurrentPointChange('discussion', newValue);
            setTimeout(() => {
              textarea.selectionStart = textarea.selectionEnd = currentLineStart;
            }, 0);
            return;
          }
          const nextNum = parseInt(numberMatch[2], 10) + 1;
          insertText = `\n${numberMatch[1]}${nextNum}. `;
        }
        
        const newValue = value.substring(0, start) + insertText + value.substring(start);
        handleCurrentPointChange('discussion', newValue);
        
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + insertText.length;
        }, 0);
      }
    }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const existingCount = (currentPoint.instructionImages?.length || 0) + (currentPoint.instructionImagesFiles?.length || 0);
    
    if (existingCount + files.length > 5) {
      setToast({ open: true, type: "error", title: "Limit Exceeded", message: "Maximum 5 images allowed per point" });
      return;
    }
    
    setCurrentPoint({
      ...currentPoint,
      instructionImagesFiles: [...(currentPoint.instructionImagesFiles || []), ...files]
    });
  };

  const handleRemoveImage = (imgIndex) => {
    const newFiles = [...currentPoint.instructionImagesFiles];
    newFiles.splice(imgIndex, 1);
    setCurrentPoint({ ...currentPoint, instructionImagesFiles: newFiles });
  };

  const handleRemoveExistingImage = (imgIndex) => {
    const newImages = [...currentPoint.instructionImages];
    newImages.splice(imgIndex, 1);
    setCurrentPoint({ ...currentPoint, instructionImages: newImages });
  };

  const handleSavePoint = () => {
    if (!currentPoint.discussion) {
      setToast({ open: true, type: "warning", title: "Missing Field", message: "Instruction / Details is required" });
      return;
    }

    const newPoints = [...formData.discussionPoints];
    if (editingIndex !== null) {
      newPoints[editingIndex] = { ...currentPoint, pointNumber: editingIndex + 1 };
    } else {
      newPoints.push({ ...currentPoint, pointNumber: newPoints.length + 1 });
    }

    setFormData({ ...formData, discussionPoints: newPoints });
    setIsAddingPoint(false);
    setEditingIndex(null);
  };
  // ----------------------------------------

  const handleSubmit = async (submitStatus = formData.status) => {
    try {
      setSaving(true);
      const submitData = new FormData();
      submitData.append("meetingDate", formData.meetingDate);
      submitData.append("status", submitStatus);
      if (isAdmin) submitData.append("adminRemarks", formData.adminRemarks);

      // Clean up points: remove file objects before stringifying
      const pointsToSubmit = formData.discussionPoints.map(p => {
        const { instructionImagesFiles, ...rest } = p;
        return rest;
      });
      submitData.append("discussionPoints", JSON.stringify(pointsToSubmit));

      // Append files
      formData.discussionPoints.forEach((point, index) => {
        if (point.instructionImagesFiles && point.instructionImagesFiles.length > 0) {
          point.instructionImagesFiles.forEach(file => {
            submitData.append(`images_${index}`, file);
          });
        }
      });

      const config = { 
        headers: { 
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "multipart/form-data"
        } 
      };

      await axios.put(`${url}/api/mom/update/${momId}`, submitData, config);
      
      setToast({ open: true, type: "success", title: "Success", message: `MOM Report updated successfully!` });
      setTimeout(() => {
        navigate(`/progress/edit-report/${projectId}`);
      }, 1500);
    } catch (err) {
      console.error("Error updating MOM:", err);
      setToast({ open: true, type: "error", title: "Error", message: err.response?.data?.message || "Failed to update MOM report" });
    } finally {
      setSaving(false);
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDelete = async () => {
    try {
      setSaving(true);
      const config = { headers: { Authorization: token ? `Bearer ${token}` : undefined } };
      await axios.delete(`${url}/api/mom/${momId}`, config);
      setToast({ open: true, type: "success", title: "Success", message: "MOM Report deleted successfully!" });
      setTimeout(() => {
        navigate(`/progress/edit-report/${projectId}`);
      }, 1500);
    } catch (err) {
      console.error("Error deleting MOM:", err);
      setToast({ open: true, type: "error", title: "Error", message: err.response?.data?.message || "Failed to delete MOM report" });
      setSaving(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.2, position: 'relative' }}>
      <AppToast {...toast} onClose={() => setToast({ ...toast, open: false })} />
      
      {/* ================= HEADER ================= */}
      <Box sx={{ mb: 3 }}>
        {/* Row 1 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
            Edit Minutes of Meeting (MOM) #{formData.serialNumber || "-"}
          </Typography>
          <IconButton size="small" onClick={() => navigate(-1)}>
            <Icon name="close3" color={COLORS.ERROR} />
          </IconButton>
        </Box>

        {/* Row 2 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Project Name:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.projectName || "-"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT }}>
              Date:
            </Typography>
            <TextField
              type="date"
              size="small"
              value={formData.meetingDate}
              onChange={(e) => handleChange("meetingDate", e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  height: "32px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Row 3 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Address:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.siteAddress || "-"}
            </Typography>
          </Box>
          <Box />
        </Box>

        {/* Row 4 */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Developer:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.developerName || "-"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Engineer:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.assignedEngineer?.name || "-"}
            </Typography>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ mt: 2, height: "1px", backgroundColor: COLORS.LIGHT_SECONDARY, opacity: 0.7 }} />
      </Box>

      {/* ================= POINTS TABLE ================= */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>Points & Actions</Typography>
          {!isAddingPoint && (
            <Button startIcon={<Icon name="plus2" />} onClick={handleOpenAddPoint} variant="outlined" size="small" sx={{ borderColor: COLORS.SECONDARY, color: COLORS.SECONDARY }}>
              Add Point
            </Button>
          )}
        </Box>
        
        {formData.discussionPoints.length > 0 && (
          <TableContainer component={Paper} sx={{ boxShadow: "none", border: `1px solid ${COLORS.LIGHT_SECONDARY}`, mb: 3 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
                <TableRow>
                  <TableCell><strong>Sr No.</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Instruction</strong></TableCell>
                  <TableCell><strong>Given By</strong></TableCell>
                  <TableCell><strong>Action By</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.discussionPoints.map((p, index) => (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>{p.pointNumber}</TableCell>
                    <TableCell>{p.pointDate ? new Date(p.pointDate).toLocaleDateString() : "-"}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-wrap", maxWidth: 300 }}>
                      <Box data-color-mode="light" sx={{ color: COLORS.PRIMARY, fontSize: "14px" }}>
                        <MDEditor.Markdown source={p.discussion} style={{ whiteSpace: 'pre-wrap', backgroundColor: 'transparent', color: 'inherit', fontSize: 'inherit' }} />
                      </Box>
                      {((p.instructionImages?.length > 0) || (p.instructionImagesFiles?.length > 0)) && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          {/* Existing */}
                          {p.instructionImages?.map((img, idx) => (
                            <Box key={`ext-${idx}`} onClick={() => setPreviewImage(`${url}${img}`)} sx={{ cursor: 'pointer', width: 40, height: 40, borderRadius: 1, overflow: 'hidden', border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                              <img src={`${url}${img}`} alt="instruction" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Box>
                          ))}
                          {/* New */}
                          {p.instructionImagesFiles?.map((file, idx) => (
                            <Box key={`new-${idx}`} onClick={() => setPreviewImage(URL.createObjectURL(file))} sx={{ cursor: 'pointer', width: 40, height: 40, borderRadius: 1, overflow: 'hidden', border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                              <img src={URL.createObjectURL(file)} alt="instruction" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Box>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{p.responsibility || "-"}</TableCell>
                    <TableCell>{p.actionable || "-"}</TableCell>
                    <TableCell>
                      <Typography sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: p.status === "Close" ? COLORS.SUCCESS : p.status === "Open" ? COLORS.WARNING : COLORS.PRIMARY,
                      }}>
                        {p.status}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton size="small" onClick={() => handleEditPoint(index)} color="primary">
                          <Icon name="edit" size={16} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeletePoint(index)} color="error">
                          <Icon name="delete" size={16} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {formData.discussionPoints.length === 0 && !isAddingPoint && (
           <Typography sx={{ textAlign: 'center', color: COLORS.LIGHT_SECONDARY, py: 4, fontStyle: 'italic' }}>
             No points added yet. Click "Add Point" to get started.
           </Typography>
        )}

        {/* INLINE POINT EDITOR */}
        {isAddingPoint && (
          <Box sx={{ p: 3, border: `1px solid ${COLORS.SECONDARY}`, borderRadius: 2, mb: 3, bgcolor: "rgba(102, 51, 153, 0.02)", position: "relative" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} pb={1} borderBottom={`1px solid ${COLORS.LIGHT_SECONDARY}`}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
                {editingIndex !== null ? `Edit Point #${editingIndex + 1}` : "Add New Point"}
              </Typography>
              <IconButton color="error" size="small" onClick={() => { setIsAddingPoint(false); setEditingIndex(null); }}>
                <Icon name="close" />
              </IconButton>
            </Box>

            {/* Row 1: Instruction */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.TEXT, mb: 0.5 }}>
                Instruction / Details *
              </Typography>
              <Box data-color-mode="light" sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: '5px', overflow: 'hidden', '.w-md-editor': { border: 'none', borderRadius: 0 } }}>
                <MDEditor
                  value={currentPoint.discussion}
                  onChange={(val) => handleCurrentPointChange("discussion", val || "")}
                  height={200}
                  preview="edit"
                  extraCommands={[]}
                  commands={[
                    commands.bold,
                    commands.italic,
                    commands.strikethrough,
                    commands.divider,
                    commands.unorderedListCommand,
                    commands.orderedListCommand,
                    commands.checkedListCommand,
                  ]}
                  textareaProps={{
                    onKeyDown: handleKeyDown
                  }}
                />
              </Box>
            </Box>

            {/* Row 2: Attachments */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: COLORS.TEXT, mb: 1 }}>Attachments</Typography>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Button variant="outlined" component="label" size="small" startIcon={<Icon name="upload" />} sx={{ bgcolor: "#fff" }}>
                  Upload Images (Max 5)
                  <input type="file" hidden multiple accept="image/*" onChange={handleImageUpload} />
                </Button>
                
                {/* Existing Images */}
                {(currentPoint.instructionImages || []).map((imgPath, imgIdx) => (
                  <Box key={`existing-${imgIdx}`} position="relative" width={60} height={60} borderRadius={1} overflow="hidden" border={`1px solid ${COLORS.LIGHT_SECONDARY}`}>
                    <img src={`${url}${imgPath}`} alt="existing" style={{ cursor: 'pointer', width: '100%', height: '100%', objectFit: 'cover' }} onClick={() => setPreviewImage(`${url}${imgPath}`)} />
                    <IconButton size="small" sx={{ position: 'absolute', top: 0, right: 0, p: 0.5, bgcolor: 'rgba(255,255,255,0.7)' }} onClick={(e) => { e.stopPropagation(); handleRemoveExistingImage(imgIdx); }}>
                      <Icon name="close" size={14} color="error" />
                    </IconButton>
                  </Box>
                ))}

                {/* New Images Preview */}
                {(currentPoint.instructionImagesFiles || []).map((file, imgIdx) => (
                  <Box key={`new-${imgIdx}`} position="relative" width={60} height={60} borderRadius={1} overflow="hidden" border={`1px solid ${COLORS.LIGHT_SECONDARY}`}>
                    <img src={URL.createObjectURL(file)} alt="preview" style={{ cursor: 'pointer', width: '100%', height: '100%', objectFit: 'cover' }} onClick={() => setPreviewImage(URL.createObjectURL(file))} />
                    <IconButton size="small" sx={{ position: 'absolute', top: 0, right: 0, p: 0.5, bgcolor: 'rgba(255,255,255,0.7)' }} onClick={(e) => { e.stopPropagation(); handleRemoveImage(imgIdx); }}>
                      <Icon name="close" size={14} color="error" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>

            <Grid container spacing={3}>
              {/* Row 3: Metadata */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Given By"
                  value={currentPoint.responsibility}
                  onChange={(e) => handleCurrentPointChange("responsibility", e.target.value)}
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", bgcolor: "#fff" } }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Action By"
                  value={currentPoint.actionable}
                  onChange={(e) => handleCurrentPointChange("actionable", e.target.value)}
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", bgcolor: "#fff" } }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={currentPoint.status}
                  onChange={(e) => handleCurrentPointChange("status", e.target.value)}
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", bgcolor: "#fff" } }}
                >
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="Close">Close</MenuItem>
                  {/* Keep legacy states in case someone edits an older mom without updating */}
                  {currentPoint.status && !["Open", "Close"].includes(currentPoint.status) && (
                     <MenuItem value={currentPoint.status}>{currentPoint.status}</MenuItem>
                  )}
                </TextField>
              </Grid>

              {currentPoint.status === "Close" && (
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Close Date"
                    type="date"
                    value={currentPoint.closeDate ? currentPoint.closeDate.split('T')[0] : ""}
                    onChange={(e) => handleCurrentPointChange("closeDate", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", bgcolor: "#fff" } }}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={currentPoint.status === "Close" ? 2 : 4}>
                <TextField
                  fullWidth
                  label="Remarks"
                  value={currentPoint.remarks}
                  onChange={(e) => handleCurrentPointChange("remarks", e.target.value)}
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", bgcolor: "#fff" } }}
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
               <Button variant="text" color="inherit" onClick={() => { setIsAddingPoint(false); setEditingIndex(null); }}>
                 Cancel
               </Button>
               <Button variant="contained" sx={{ bgcolor: COLORS.SECONDARY }} onClick={handleSavePoint}>
                 Save Point
               </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* 
      {isAdmin && (
        <Box sx={{ p: 3, border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 2, mb: 3, borderLeft: `4px solid ${COLORS.ERROR}` }}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 2 }}>Admin Review</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Report Status" value={formData.status} onChange={(e) => handleChange("status", e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}>
                <MenuItem value="DRAFT">DRAFT</MenuItem>
                <MenuItem value="SUBMITTED">SUBMITTED</MenuItem>
                <MenuItem value="APPROVED">APPROVED</MenuItem>
                <MenuItem value="REJECTED">REJECTED</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" label="Admin Remarks" value={formData.adminRemarks} onChange={(e) => handleChange("adminRemarks", e.target.value)} multiline rows={2} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }} />
            </Grid>
          </Grid>
        </Box>
      )}
      */}

      {/* ================= ACTIONS ================= */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 2, borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
        <Button 
          variant="outlined" 
          color="error" 
          onClick={() => setDeleteDialogOpen(true)} 
          disabled={saving || isAddingPoint}
          startIcon={<Icon name="delete" />}
        >
          Delete
        </Button>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate(-1)} sx={{ borderColor: COLORS.LIGHT_SECONDARY, color: COLORS.TEXT }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => handleSubmit()} disabled={saving || isAddingPoint} sx={{ bgcolor: COLORS.PRIMARY, "&:hover": { bgcolor: COLORS.PRIMARY }, textTransform: "none" }}>
            Save Changes
          </Button>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ ...TYPOGRAPHY.H3, color: COLORS.ERROR }}>
          Delete MOM Report
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: COLORS.TEXT }}>
            Are you sure you want to delete this MOM report? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: COLORS.TEXT }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : "Delete Report"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage}
        title="Image Preview"
      />
    </Box>
  );
}
